import { Routes, Route, Navigate } from "react-router";
import { LoginPage } from "./pages/login/LoginPage";
import { SignupPage } from "./pages/signup/SignupPage";
import { SetupPasswordPage } from "./pages/setup/SetupPasswordPage";
import { ForgotPasswordPage } from "./pages/forgot/ForgotPasswordPage";
import { AcceptInvitePage } from "./pages/invite/AcceptInvitePage";
import { ShellLayout } from "./layouts/ShellLayout";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { UsersPage } from "./pages/users/UsersPage";
import { UserDetailPage } from "./pages/users/UserDetailPage";
import { InstitutionsPage } from "./pages/institutions/InstitutionsPage";
import { InstitutionDetailPage } from "./pages/institutions/InstitutionDetailPage";
import { OrganogramaPage } from "./pages/org/OrganogramaPage";
import { ApprovalsPage } from "./pages/approvals/ApprovalsPage";
import { ModulesPage } from "./pages/modules/ModulesPage";
import { LicensesPage } from "./pages/licenses/LicensesPage";
import { PaymentsPage } from "./pages/payments/PaymentsPage";
import { FinanceiroPage } from "./pages/financial/FinanceiroPage";
import { AuditoriaPage } from "./pages/audit/AuditoriaPage";
import { RoleGroupsPage } from "./pages/role-groups/RoleGroupsPage";
import { ProfilePage } from "./pages/profile/ProfilePage";
import { useAuth } from "./api/auth-context";
import { TenantGate } from "./components/TenantGate";
import { AjudaPage } from "./pages/ajuda/AjudaPage";
import type { JSX } from "react";

function PrivateRoute({ children }: { children: JSX.Element }) {
  const { accessToken, ready } = useAuth();
  if (!ready) return null; // aguarda /auth/refresh inicial — evita flicker para /login
  if (!accessToken) return <Navigate to="/login" replace />;
  return <TenantGate>{children}</TenantGate>;
}

/** Gestão de usuários/instituições/licenças/pagamentos: master OU admin de instituição. */
function RequireManage({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const canManage = !!user?.isMasterAdmin || (user?.adminInstitutionIds?.length ?? 0) > 0;
  if (!canManage) return <Navigate to="/" replace />;
  return children;
}

/** Telas exclusivas do master admin (módulos, aprovações, auditoria, financeiro, grupos). */
function RequireMaster({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  if (!user?.isMasterAdmin) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/setup" element={<SetupPasswordPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/invite" element={<AcceptInvitePage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <ShellLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="users" element={<RequireManage><UsersPage /></RequireManage>} />
        <Route path="users/:id" element={<RequireManage><UserDetailPage /></RequireManage>} />
        <Route path="institutions" element={<RequireManage><InstitutionsPage /></RequireManage>} />
        <Route path="institutions/:id" element={<RequireManage><InstitutionDetailPage /></RequireManage>} />
        <Route path="organograma" element={<OrganogramaPage />} />
        <Route path="approvals" element={<RequireMaster><ApprovalsPage /></RequireMaster>} />
        <Route path="modules" element={<RequireMaster><ModulesPage /></RequireMaster>} />
        <Route path="licenses" element={<RequireManage><LicensesPage /></RequireManage>} />
        <Route path="role-groups" element={<RequireMaster><RoleGroupsPage /></RequireMaster>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="payments" element={<RequireManage><PaymentsPage /></RequireManage>} />
        <Route path="financial" element={<RequireMaster><FinanceiroPage /></RequireMaster>} />
        <Route path="audit" element={<RequireMaster><AuditoriaPage /></RequireMaster>} />
        <Route path="ajuda/:doc" element={<AjudaPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
